import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { Resolver } from 'react-hook-form/dist/types'
import { toast } from 'react-toastify'

import {
  useCreateTrackedProcessMutation,
  useDeleteTrackedProcessMutation,
  useUpdateTrackedProcessMutation,
} from '../../app/apiSlice'
import { InlineLoader } from '../../common/components'
import { ActionModal, Button, Divider, Tabs } from '../../common/designs'
import type { ProcessAnalysisConfig } from '../Processes/types'
import ProcessFormAdvancedTabContent from './ProcessFormAdvancedTabContent'
import ProcessFormBasicTabFields from './ProcessFormBasicTabFields'
import { PROCESS_CONFIG_TABS } from './constants'
import type { ProcessFormFields } from './types'
import { buildProcessAnalysisConfig, processFormResolver } from './utils'
import useGetIdentifierOptions from './useIdentifierOptions'
import { useProcessFormStepMatchingState } from './useProcessFormStepMatchingState'

interface Props {
  editedData?: ProcessAnalysisConfig | null
  onClose: () => void
}

const ProcessForm = ({ editedData, onClose }: Props) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false)
  const [activeConfigTab, setActiveConfigTab] = useState<string>('basic')

  const [isSelectUsedWithIdName, setIsSelectUsedWithIdName] = useState<boolean>(true)
  const [isSelectUsedWithStateTag, setIsSelectUsedWithStateTag] = useState<boolean>(true)

  const stepMatching = useProcessFormStepMatchingState(editedData)

  const { data: dimensionOptions, isLoading: isLoadingDimensionOptions } = useGetIdentifierOptions()
  const [createTrackedProcess, { isLoading: isLoadingCreate }] = useCreateTrackedProcessMutation()
  const [updateTrackedProcess, { isLoading: isLoadingEdit }] = useUpdateTrackedProcessMutation()
  const [deleteTrackedProcess, { isLoading: isLoadingDelete }] = useDeleteTrackedProcessMutation()

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<ProcessFormFields>({
    resolver: processFormResolver as Resolver<ProcessFormFields>,
  })

  useEffect(() => {
    const initialData = editedData
      ? {
          name: editedData.name,
          process_identifier_name: editedData.process_identifier_name,
          process_category: editedData.process_category,
          initial_visible_variant_amount: editedData.initial_visible_variant_amount,
          state_tag_key: editedData.state_tag_key,
        }
      : {
          name: '',
          process_identifier_name: '',
          process_category: '',
          initial_visible_variant_amount: 5,
          state_tag_key: '',
        }
    reset(initialData)
  }, [editedData, reset])

  useEffect(() => {
    if (!editedData) {
      return
    }

    const dimensionValues = dimensionOptions.map(({ value }) => value)

    setIsSelectUsedWithIdName(dimensionValues.includes(editedData.process_identifier_name))
    setIsSelectUsedWithStateTag(dimensionValues.includes(editedData.state_tag_key))
  }, [dimensionOptions, editedData, setIsSelectUsedWithIdName, setIsSelectUsedWithStateTag])

  const buildPayload = (formData: ProcessFormFields) =>
    buildProcessAnalysisConfig(
      formData,
      {
        prev: stepMatching.stepsInheritingIdFromPrevWindow,
        next: stepMatching.stepsInheritingIdFromNextWindow,
        multi: stepMatching.stepsInheritingIdWhenInMultiSystemFlow,
      },
      stepMatching.rowIdsPendingFirstApply,
    )

  const onSave = (formData: ProcessFormFields) => {
    createTrackedProcess(buildPayload(formData))
      .unwrap()
      .then(() => {
        toast.success('Process created successfully')
        onClose()
      })
  }

  const onDelete = () => {
    if (!editedData) return

    deleteTrackedProcess(editedData.id)
      .unwrap()
      .then(() => onClose())
  }

  const onEdit = (formData: ProcessFormFields) => {
    if (!editedData) return

    updateTrackedProcess({
      processId: editedData.id,
      body: {
        id: editedData.id,
        ...buildPayload(formData),
      },
    })
      .unwrap()
      .then(() => {
        toast.success('Process saved successfully')
        onClose()
      })
  }

  const onSubmitForm = (formProps: ProcessFormFields) => {
    editedData ? onEdit(formProps) : onSave(formProps)
  }

  return (
    <>
      {isLoadingDimensionOptions ? (
        <InlineLoader />
      ) : (
        <>
          <form
            className='flex flex-col gap-4'
            autoComplete='off'
            onSubmit={handleSubmit(onSubmitForm)}
          >
            <Tabs
              size='s'
              options={PROCESS_CONFIG_TABS}
              value={activeConfigTab}
              onChange={(val: string) => setActiveConfigTab(val)}
            />

            {activeConfigTab === 'basic' ? (
              <ProcessFormBasicTabFields
                control={control}
                errors={errors}
                dimensionOptions={dimensionOptions}
                isSelectUsedWithIdName={isSelectUsedWithIdName}
                setIsSelectUsedWithIdName={setIsSelectUsedWithIdName}
                isSelectUsedWithStateTag={isSelectUsedWithStateTag}
                setIsSelectUsedWithStateTag={setIsSelectUsedWithStateTag}
              />
            ) : null}

            {activeConfigTab === 'advanced' ? (
              <ProcessFormAdvancedTabContent
                stepsInheritingIdFromPrevWindow={stepMatching.stepsInheritingIdFromPrevWindow}
                setStepsInheritingIdFromPrevWindow={stepMatching.setStepsInheritingIdFromPrevWindow}
                stepsInheritingIdFromNextWindow={stepMatching.stepsInheritingIdFromNextWindow}
                setStepsInheritingIdFromNextWindow={stepMatching.setStepsInheritingIdFromNextWindow}
                stepsInheritingIdWhenInMultiSystemFlow={
                  stepMatching.stepsInheritingIdWhenInMultiSystemFlow
                }
                setStepsInheritingIdWhenInMultiSystemFlow={
                  stepMatching.setStepsInheritingIdWhenInMultiSystemFlow
                }
                onRegisterNewRowNeedsApply={stepMatching.onRegisterNewRowNeedsApply}
                onRowRuleApplied={stepMatching.onRowRuleApplied}
                onRowRuleRemoved={stepMatching.onRowRuleRemoved}
              />
            ) : null}

            <Divider color='lightestGray' className='my-0.5' />

            <div className='flex flex-wrap justify-end gap-3'>
              <Button variant='white' onClick={onClose}>
                Cancel
              </Button>

              {editedData ? (
                <Button variant='destructive' onClick={() => setIsConfirmationModalOpen(true)}>
                  Delete
                </Button>
              ) : null}

              <Button type='submit' disabled={isLoadingCreate || isLoadingEdit}>
                Save
              </Button>
            </div>
          </form>
        </>
      )}

      <ActionModal
        variant='destructive'
        label='Are you sure you want to delete the tracking?'
        actionLabel='Delete'
        open={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onAction={() => (editedData ? onDelete() : null)}
        loading={isLoadingDelete}
      />
    </>
  )
}

export default ProcessForm

